import { Amplify } from "aws-amplify";
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  fetchUserAttributes,
  getCurrentUser,
  fetchAuthSession,
  updatePassword,
  resetPassword,
  confirmResetPassword
} from "aws-amplify/auth";
import { showToast } from "../toast/toast.service";
import { ForgotPassword } from "@/components/modules/auth/types";

/**
 *
 * limitation @link https://github.com/aws-amplify/amplify-flutter/issues/1902
 */

export const initAmplify = () => {
  Amplify.configure({
    Auth: {
      Cognito: {
        // REQUIRED - Amazon Cognito User Pool ID
        userPoolId: import.meta.env.VITE_APP_AUTH_USER_POOL_ID,

        // OPTIONAL - Amazon Cognito Web Client ID (26-char alphanumeric string)
        userPoolClientId: import.meta.env.VITE_APP_AUTH_CLIENT_ID,

        // REQUIRED - Amazon Cognito Region
        identityPoolId: import.meta.env.VITE_APP_AUTH_IDENTITY_POOL_ID,

        // OPTIONAL - Set to true to use cookies for authentication
        loginWith: {
          email: true
        }
      }
    },
    Storage: {
      S3: {
        bucket: import.meta.env.VITE_APP_S3_BUCKET,
        region: import.meta.env.VITE_APP_AUTH_REGION
      }
    }
  });
};

export const signIn = async (email: string, password: string) => {
  return await amplifySignIn({ username: email, password });
};

export const signOut = async () => {
  try {
    await amplifySignOut();
  } catch (error) {
    showToast("error", "Can't sign out right now!", 1000);
  }
};

export const getUser = async () => {
  try {
    const user = await fetchUserAttributes();
    if (user && Object.keys(user).length) {
      return user;
    } else {
      throw new Error("User not found");
    }
  } catch (error) {
    throw new Error("User not found");
  }
};

export const getAuthenticatedUser = async () => {
  return await getCurrentUser();
};

export const getAuthJWTToken = async () => {
  const session = await fetchAuthSession();
  const token = session.tokens?.accessToken?.toString();

  if (!token) {
    throw new Error("No access token available");
  }

  return token;
};

export const refreshToken = async () => {
  return await fetchAuthSession({ forceRefresh: true });
};

export const changePassword = async (
  oldPassword: string,
  newPassword: string
) => {
  return await updatePassword({ oldPassword, newPassword });
};

export const forgotPasswordSubmit = async (payload: ForgotPassword) => {
  return await confirmResetPassword({
    username: payload.username,
    confirmationCode: payload.code,
    newPassword: payload.new_password
  });
};

export const forgotPassword = async (userName: string) => {
  return await resetPassword({ username: userName })
    .then((response) => response)
    .catch((err) => {
      throw err;
    });
};
